import { colors } from '@EVOCORP/corechat-theme';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import { alpha, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Zoom from '@material-ui/core/Zoom';
import ImageIcon from '@material-ui/icons/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import MovieOutlinedIcon from '@material-ui/icons/MovieOutlined';
import PlayArrowOutlinedIcon from '@material-ui/icons/PlayArrowOutlined';
import ReactPlayer, { ReactPlayerProps } from 'react-player';

import {
  MessageHeaderFormatsTemplate,
  WppComponentsType,
} from 'store/reducers/templates';
import { getFileNameAndExtension } from 'utils/getFileNameAndExtension';
import parseTextToHtml from 'utils/parseTextToHtml';
import { replaceTemplateVariables } from 'utils/replaceTemplateVariables';

import ImageViewer from './ImageViewer';

const useStyles = makeStyles(theme => ({
  placeholder: {
    width: '100%',
    height: theme.spacing(10),
    backgroundColor: colors.light.background.default,
    borderRadius: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageHeader: {
    width: '100%',
    backgroundColor: colors.light.background.default,
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden',
  },
  documentHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
    minWidth: 40,
    paddingBottom: theme.spacing(1),
  },
  documentListItem: {
    color: 'inherit',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: colors.light.background.default,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  documentListItemAvatar: {
    minWidth: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  documentFileName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 11,
  },
  playButton: {
    color: theme.palette.getContrastText(
      alpha(theme.palette.common.black, 0.4),
    ),
    backgroundColor: `${alpha(theme.palette.common.black, 0.4)} !important`,
  },
  textHeader: {
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
    fontWeight: 600,
    color: colors.light.text.primary,
  },
}));

interface HeaderVariable {
  name?: string;
  value: string;
}

export interface TemplateHeaderProps {
  templateHeaderData: WppComponentsType;
  readOnly?: boolean;
  currentUrl?: string | null;
  headerVariables?: HeaderVariable[];
}

function HeaderPlaceholder({ icon }: { icon: React.ReactNode }) {
  const classes = useStyles();
  return <Box className={classes.placeholder}>{icon}</Box>;
}

function ImageHeader({
  mediaUrl,
  readOnly,
}: {
  mediaUrl: string;
  readOnly?: boolean;
}) {
  const classes = useStyles();
  return (
    <Zoom in mountOnEnter unmountOnExit timeout={400}>
      <Box
        className={classes.imageHeader}
        style={{ height: readOnly ? 150 : 100 }}
      >
        <ImageViewer src={mediaUrl}>
          <img
            src={mediaUrl}
            alt="Header preview"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </ImageViewer>
      </Box>
    </Zoom>
  );
}

function DocumentHeader({ mediaUrl }: { mediaUrl: string }) {
  const classes = useStyles();
  const { name, extension } = getFileNameAndExtension(mediaUrl);
  const fileName = [
    decodeURIComponent(name),
    extension ? decodeURIComponent(extension) : '',
  ]
    .filter(Boolean)
    .join('.');

  return (
    <Box className={classes.documentHeader}>
      <ListItem
        component={Link}
        target="_blank"
        rel="noopener noreferrer"
        href={mediaUrl}
        disableGutters
        className={classes.documentListItem}
      >
        <ListItemAvatar className={classes.documentListItemAvatar}>
          <InsertDriveFileOutlinedIcon fontSize="medium" />
        </ListItemAvatar>

        <Typography className={classes.documentFileName}>{fileName}</Typography>
      </ListItem>
    </Box>
  );
}

function VideoHeader({
  mediaUrl,
  readOnly,
}: {
  mediaUrl: string;
  readOnly?: boolean;
}) {
  const classes = useStyles();
  const playerOptions: ReactPlayerProps = {
    url: mediaUrl,
    controls: true,
    width: '100%',
    height: readOnly ? 150 : 100,
    style: {
      backgroundColor: 'black',
    },
    playIcon: (
      <IconButton className={classes.playButton}>
        <PlayArrowOutlinedIcon fontSize="large" />
      </IconButton>
    ),
  };

  return <ReactPlayer {...playerOptions} />;
}

function TextHeader({
  text,
  headerVariables,
  readOnly,
}: {
  text: string;
  headerVariables: HeaderVariable[];
  readOnly?: boolean;
}) {
  const classes = useStyles();
  const processedText = replaceTemplateVariables(text, headerVariables);

  return (
    <Typography
      variant="caption"
      color="textPrimary"
      className={classes.textHeader}
      style={{ fontSize: readOnly ? 12 : 13 }}
      dangerouslySetInnerHTML={{ __html: parseTextToHtml(processedText) }}
    />
  );
}

function TemplateHeader({
  templateHeaderData,
  readOnly = false,
  currentUrl = null,
  headerVariables = [],
}: TemplateHeaderProps) {
  const exampleUrl = templateHeaderData?.example?.header_handle?.[0] ?? null;
  const mediaUrl = readOnly ? exampleUrl : currentUrl;

  const { format } = templateHeaderData ?? {};

  if (format === MessageHeaderFormatsTemplate.IMAGEM) {
    return mediaUrl ? (
      <ImageHeader mediaUrl={mediaUrl} readOnly={readOnly} />
    ) : (
      <HeaderPlaceholder
        icon={<ImageIcon style={{ color: colors.light.primary.main }} />}
      />
    );
  }

  if (format === MessageHeaderFormatsTemplate.DOCUMENTO) {
    return mediaUrl ? (
      <DocumentHeader mediaUrl={mediaUrl} />
    ) : (
      <HeaderPlaceholder
        icon={
          <InsertDriveFileOutlinedIcon
            style={{ color: colors.light.primary.main }}
          />
        }
      />
    );
  }

  if (format === MessageHeaderFormatsTemplate.VIDEO) {
    return mediaUrl ? (
      <VideoHeader mediaUrl={mediaUrl} readOnly={readOnly} />
    ) : (
      <HeaderPlaceholder
        icon={
          <MovieOutlinedIcon style={{ color: colors.light.primary.main }} />
        }
      />
    );
  }

  if (templateHeaderData?.text) {
    return (
      <TextHeader
        readOnly={readOnly}
        text={templateHeaderData.text}
        headerVariables={headerVariables}
      />
    );
  }

  return null;
}

export default TemplateHeader;
